import posthog from 'posthog-js'
import { onBeforeUnmount, readonly, ref } from 'vue'

export const usePosthogFeatureFlagsReady = () => {
  const isReady = ref(posthog.featureFlags?.hasLoadedFlags ?? false)

  if (!isReady.value) {
    const unsubscribe = posthog.onFeatureFlags(() => {
      isReady.value = true
    })
    onBeforeUnmount(() => unsubscribe())
  }

  return readonly(isReady)
}

export const usePosthogFeatureFlag = (flag, options) => {
  const isEnabled = ref(false)

  const unsubscribe = posthog.onFeatureFlags(() => {
    isEnabled.value = posthog.isFeatureEnabled(flag, options)
  })

  onBeforeUnmount(() => unsubscribe())

  return readonly(isEnabled)
}

/**
 * Every resolved flag, keyed by flag key. Use it when the flags to evaluate are not known
 * upfront — a config-driven list, for example — so callers do not have to name each one.
 * Prefer `usePosthogFeatureFlag` when you know the flag at author time.
 *
 * Each key is evaluated through `isFeatureEnabled`, the same call every other gate uses, so
 * a caller never has to interpret a raw flag value itself. Note a multivariate flag counts as
 * enabled for *every* variant, `control` included — a flag used for gating must be boolean.
 * @returns {Readonly<import('vue').Ref<Record<string, boolean>>>}
 */
export const usePosthogFeatureFlags = () => {
  const readFlags = () => {
    const flags = posthog.featureFlags?.getFlags?.() ?? []

    return Object.fromEntries(
      flags.map((flag) => [flag, !!posthog.isFeatureEnabled(flag)])
    )
  }

  const flags = ref(readFlags())

  const unsubscribe = posthog.onFeatureFlags(() => {
    flags.value = readFlags()
  })

  onBeforeUnmount(() => unsubscribe())

  return readonly(flags)
}
